import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Field } from 'components/uikit/Field';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { Loading } from 'components/uikit/suspense/Loading';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { QueryFactory } from 'services/api';
import { UrlType, VariableListItemDto } from 'services/api/api-client';
import { UrlTypeDropDownInput } from '../urlTypeDropDownInput/UrlTypeDropDownInput';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import styles from './MergeVariableModal.module.scss';
import { HookFormDropDownInput } from 'components/uikit/inputs/dropdown/HookFormDropDownInput';
import dropdownStyles from 'components/uikit/DropdownSelector.module.scss';
import clsx from 'clsx';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import DocumentIcon from 'assets/icons/document-icon.svg?react';
import { queryClient } from 'services/api/query-client-helper';
import { useDocumentContext } from 'pages/authorized/documents/documentContext/DocumentContext';
import { useVariablesContext } from '../../variablesContext/VariablesContext';

type MergeVariableModalData = {
  urlType: UrlType;
  sourceVariable: VariableListItemDto;
};

export const MergeVariableModal: React.FC<{
  targetVariableId: number;
  projectId: number;
  revisionId: number;
  onClose: () => void;
  isOpen: boolean;
}> = (props) => {
  const i18n = useScopedTranslation('Component.MergeVariableModal');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const { setDocumentState } = useDocumentContext();

  const variableQuery = QueryFactory.VariableQuery.useGetQuery(
    {
      id: props.targetVariableId,
      revisionId: props.revisionId,
    },
    {
      enabled: props.isOpen,
    },
  );

  const { allVariablesQuery: variablesToMergeQuery, refreshVariables } =
    useVariablesContext();

  const form = useAdvancedForm<MergeVariableModalData>(
    useCallback(
      async (data) => {
        setIsLoading(true);

        await QueryFactory.VariableClient.merge({
          targetVariableId: props.targetVariableId,
          sourceVariableId: data.sourceVariable.id,
          revisionId: props.revisionId,
        });

        await Promise.all([
          ...targetedDocuments.map(
            async (doc) =>
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.DocumentQuery.getQueryKey(doc.id),
              }),
          ),
          refreshVariables(),
        ]);

        setDocumentState((prevState) => ({
          ...prevState,
          enableDocumentUpdate: true,
        }));
        onClose();
        setIsLoading(false);
      },
      [props, variableQuery.data, variablesToMergeQuery.data],
    ),
    {
      defaultValues: {
        urlType: variableQuery.data?.actualValue.urlType || UrlType.Default,
      },
    },
  );

  const onClose = () => {
    props.onClose();
    setIsConfirming(false);
    form.reset();
  };

  const beforeMerge = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!sourceVariable) {
      form.setError('sourceVariable', {
        message: requiredRule().required.message,
      });
      return;
    }

    setIsConfirming(true);
  };

  const targetedDocuments = useMemo(() => {
    if (!variableQuery.data) return [];
    return variableQuery.data.documents || [];
  }, [variableQuery.data]);

  const urlType = form.watch('urlType');
  const sourceVariable = form.watch('sourceVariable');

  useEffect(() => {
    form.clearErrors('sourceVariable');
  }, [sourceVariable]);

  const variablesToMerge = useMemo(() => {
    if (!variablesToMergeQuery.data) return [];
    return variablesToMergeQuery.data.data.filter(
      (v) =>
        v.id !== props.targetVariableId &&
        (v.actualValue.urlType === urlType ||
          (urlType === UrlType.Default && !v.actualValue.urlType)) &&
        (v.documentId === null ||
          v.documentId === variableQuery.data?.documentId),
    );
  }, [urlType, props.targetVariableId, variablesToMergeQuery.data]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onClose}
      title={i18n.t('title')}
      isBlocking={true}
    >
      <Loading
        loading={
          variableQuery.isLoading ||
          variablesToMergeQuery.isLoading ||
          isLoading
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div
            className={
              isConfirming
                ? styles.formTextConfirm
                : styles.formTextBeforeConfirm
            }
          >
            <Trans
              i18nKey={
                isConfirming
                  ? 'Component.MergeVariableModal.confirm_message'
                  : 'Component.MergeVariableModal.message'
              }
              values={{
                targetVariable: variableQuery?.data?.name,
                sourceVariable: sourceVariable?.name,
                targetVariableId: variableQuery?.data?.id,
                sourceVariableId: sourceVariable?.id,
              }}
              components={{
                bold: <strong />,
                grey: <span className={styles.optionId} />,
              }}
            />
          </div>

          <div className={styles.formWrapper}>
            {isConfirming ? (
              <div className={styles.documentsList}>
                {targetedDocuments.map((element) => (
                  <div key={element.id} className={styles.documentItem}>
                    <DocumentIcon /> {element.name}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <Field title={i18n.t('Fields.url_type')}>
                  <UrlTypeDropDownInput
                    name={'urlType'}
                    formControl={form.control}
                    errorMessage={form.formState.errors.urlType?.message}
                  />
                </Field>

                <Field title={i18n.t('Fields.source_variable')}>
                  <HookFormDropDownInput
                    name={'sourceVariable'}
                    className={clsx(
                      styles.urlTypeSelector,
                      dropdownStyles.selector,
                    )}
                    options={variablesToMerge}
                    getOptionLabel={(variable) => variable.name}
                    classes={dropdownClasses}
                    endAdornmentClassname={styles.expandIcon}
                    control={form.control}
                    required={true}
                    errorText={form.formState.errors.sourceVariable?.message}
                    enableSearch={true}
                    filterOptions={(options, state) => {
                      return options.filter(
                        (option) =>
                          option.name
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase()) ||
                          option.id.toString().includes(state.inputValue),
                      );
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.id}
                        className={clsx(styles.option, props.className)}
                      >
                        <span className={styles.optionName}>{option.name}</span>
                        <span className={styles.optionId}>({option.id})</span>
                      </li>
                    )}
                  />
                </Field>
              </>
            )}

            <div className={styles.footer}>
              <Button
                className={styles.button}
                color={ButtonColor.Secondary}
                title={i18n.t('cancel_button')}
                onClick={async () => onClose()}
              />
              {isConfirming ? (
                <Button
                  className={styles.button}
                  color={ButtonColor.Default}
                  type={'submit'}
                  title={i18n.t('confirm_button')}
                />
              ) : (
                <Button
                  className={styles.button}
                  color={ButtonColor.Default}
                  title={i18n.t('merge_button')}
                  onClick={beforeMerge}
                />
              )}
            </div>
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};

const dropdownClasses = {
  option: dropdownStyles.selectorOption,
  paper: dropdownStyles.selectorPaper,
  popper: dropdownStyles.selectorPopper,
  input: styles.sourceVaribleSelector,
};
